import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import type { GenerateCandidateResponse, SaveGeneratedImagePayload, SaveImageResponse } from "./entityImageApi";
import type { ItemSaveStatus, ReferenceImagePayload, StylePresetOption, TileState } from "./types";

export type GenerationStatus = "idle" | "generating" | "saving";

export interface MultiSaveResult {
  succeededIds: string[];
  failedIds: string[];
}

type TileOutcome = "success" | "error" | "success_unsaved";

export interface GenerateTileRequest {
  stylePresetId: string;
  stylePresetText?: string;
  referenceImage?: ReferenceImagePayload | null;
  referenceImageGuidance?: string;
  userDescription?: string;
  batchId?: string;
}

/**
 * Everything entity-specific about generating and saving images. Built per entity (the entity id
 * is closed over), so the hook itself never needs to know which kind of entity it serves.
 */
export interface EntityImageGenerationAdapter<TSummary, TBatchParams> {
  listStylePresets(): Promise<{ stylePresets: StylePresetOption[]; defaultStyleIds?: string[]; defaultBatchParams?: TBatchParams }>;
  summarize(input: { description: string; referenceImage: ReferenceImagePayload | null | undefined }): Promise<TSummary>;
  generate(summary: TSummary, request: GenerateTileRequest, batchParams: TBatchParams | undefined): Promise<GenerateCandidateResponse>;
  saveGenerated(payload: SaveGeneratedImagePayload): Promise<SaveImageResponse>;
  upload(payload: ReferenceImagePayload): Promise<SaveImageResponse>;
  analytics: {
    generateClicked(props: { styleCount: number; isFreeformStyle: boolean; isRegenerate: boolean; hasReferenceImage: boolean; descriptionLength: number }, batchParams: TBatchParams | undefined): void;
    tileResult(props: { stylePresetId: string; isFreeformStyle: boolean; isRetry: boolean; hasReferenceImage: boolean; outcome: TileOutcome }): void;
    retryTileClicked(props: { stylePresetId: string; isFreeformStyle: boolean; hasReferenceImage: boolean }): void;
    saveBatchCompleted(props: { source: "upload"; itemCount: number; succeededCount: number; failedCount: number; becameCover: boolean }): void;
  };
  /** Prefix for Sentry `feature` tags, e.g. "character_portrait" → "character_portrait_generate". */
  sentryFeaturePrefix: string;
  /**
   * The style used when no presets are selected, whose tile sends the description as free-form
   * style text. Null means zero selected styles can't be generated.
   */
  freeformStyle: { id: string; label: string } | null;
  defaultStyleIds: string[];
  maxSelectedStyles: number;
  copy: {
    allFailed: string;
    allFailedWithReference: string;
    someFailedWithReference: string;
    unsavedTiles: string;
  };
}

function buildSaveErrorMessage(failedCount: number, totalCount: number): string {
  if (totalCount === 1) return "Couldn't save — please try again.";
  if (failedCount === totalCount) return "Couldn't save those images — please try again.";
  return `Couldn't save ${failedCount} of ${totalCount} images — please try again.`;
}

function makePlaceholderTiles(selectedStyleIds: string[], stylePresets: StylePresetOption[] | null, freeformStyle: { id: string; label: string } | null): TileState[] {
  if (selectedStyleIds.length === 0) {
    return freeformStyle ? [{ stylePresetId: freeformStyle.id, stylePresetLabel: freeformStyle.label, status: "loading" as const }] : [];
  }
  const labelById = new Map((stylePresets ?? []).map((preset) => [preset.id, preset.label]));
  return selectedStyleIds.map((id) => ({
    stylePresetId: id,
    stylePresetLabel: labelById.get(id) ?? "",
    status: "loading" as const,
  }));
}

export interface UseEntityImageGenerationResult<TBatchParams = undefined> {
  status: GenerationStatus;
  isGenerating: boolean;
  isSaving: boolean;
  itemSaveStatuses: Record<string, ItemSaveStatus>;
  description: string;
  setDescription: (value: string) => void;
  selectedStyleIds: string[];
  toggleStyle: (styleId: string) => void;
  /** False when nothing is selected and the entity has no freeform style to fall back to. */
  hasGeneratableSelection: boolean;
  stylePresets: StylePresetOption[] | null;
  stylePresetsError: string | null;
  /** Batch params suggested alongside the style presets (e.g. the entity type's aspect ratio); undefined until they load. */
  defaultBatchParams: TBatchParams | undefined;
  ensureStylePresetsLoaded: () => void;
  referenceImageGuidance: string;
  setReferenceImageGuidance: (value: string) => void;
  tiles: TileState[] | null;
  /** The batch params (e.g. aspect ratio) the current tiles were generated with. */
  tilesBatchParams: TBatchParams | undefined;
  summarizeError: string | null;
  generateError: string | null;
  unsavedTileError: string | null;
  saveError: string | null;
  handleGenerate: (referenceImagePayload?: ReferenceImagePayload | null, batchParams?: TBatchParams) => Promise<void>;
  handleRetryTile: (stylePresetId: string) => void;
  saveImportedImages: (items: { id: string; payload: ReferenceImagePayload }[]) => Promise<MultiSaveResult | null>;
}

export function useEntityImageGeneration<TSummary, TBatchParams = undefined>(
  adapter: EntityImageGenerationAdapter<TSummary, TBatchParams>,
  {
    onSaved,
    onGenerated,
    onCoverSet,
    stylePresetsKey,
  }: {
    onSaved?: (result: { imageId: string; imageUrl: string } | null, meta: { hadFailures: boolean }) => void;
    /** Fired when a generate batch (or a single Retry) saved at least one tile. */
    onGenerated?: () => void;
    /** Fired when the server made a just-saved image the cover (the entity's first image). */
    onCoverSet?: (cover: { imageId: string; imageUrl: string }) => void;
    /** Whatever the server's style presets depend on (e.g. the entity's type); a change discards the loaded presets and untouched selection. */
    stylePresetsKey?: string;
  } = {},
): UseEntityImageGenerationResult<TBatchParams> {
  const { analytics, freeformStyle, sentryFeaturePrefix } = adapter;
  const freeformStyleId = freeformStyle?.id ?? null;

  const [phase, setPhase] = useState<"idle" | "saving">("idle");
  const [itemSaveStatuses, setItemSaveStatuses] = useState<Record<string, ItemSaveStatus>>({});
  const [description, setDescription] = useState("");
  const [selectedStyleIds, setSelectedStyleIdsState] = useState<string[]>(adapter.defaultStyleIds);
  const selectionTouchedRef = useRef(false);
  const [stylePresets, setStylePresets] = useState<StylePresetOption[] | null>(null);
  const [stylePresetsError, setStylePresetsError] = useState<string | null>(null);
  const stylePresetsKeyRef = useRef(stylePresetsKey);
  stylePresetsKeyRef.current = stylePresetsKey;
  // The key the last started fetch was for; null until one starts (or after it fails).
  const stylePresetsFetchedKeyRef = useRef<{ key: string | undefined } | null>(null);
  const [defaultBatchParams, setDefaultBatchParams] = useState<TBatchParams | undefined>(undefined);
  const [referenceImageGuidance, setReferenceImageGuidance] = useState("");
  const [tiles, setTiles] = useState<TileState[] | null>(null);
  const [tilesBatchParams, setTilesBatchParams] = useState<TBatchParams | undefined>(undefined);
  const [summarizeError, setSummarizeError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const lastSummaryRef = useRef<TSummary | null>(null);
  const hadReferenceImageRef = useRef(false);
  const onCoverSetRef = useRef(onCoverSet);
  onCoverSetRef.current = onCoverSet;
  const notifyCoverSet = useCallback((saved: { imageId?: string | null; imageUrl?: string | null; isCover?: boolean }) => {
    if (saved.isCover && saved.imageId && saved.imageUrl) onCoverSetRef.current?.({ imageId: saved.imageId, imageUrl: saved.imageUrl });
  }, []);
  // Rejects a second near-simultaneous invocation synchronously — `status` is derived from React
  // state, so two fast clicks before a re-render can both see "idle". Cleared imperatively
  // (try/finally below), not via a `status` effect, since tile updates can settle generating→idle
  // within one commit and never trigger that effect.
  const inFlightRef = useRef(false);
  // Per-tile version of inFlightRef, since multiple tiles can legitimately retry concurrently.
  const retryInFlightIdsRef = useRef<Set<string>>(new Set());
  // Snapshot of the last handleGenerate() batch's inputs, so a later Retry reproduces the same
  // request even if description/reference image/style selection/batch params have since changed.
  const lastGenerateInputsRef = useRef<{
    referenceImagePayload?: ReferenceImagePayload | null;
    referenceImageGuidance?: string;
    description: string;
    batchId: string;
    batchParams?: TBatchParams;
  } | null>(null);

  const isGenerating = tiles !== null && tiles.some((tile) => tile.status === "loading");
  const isSaving = phase === "saving";
  const status: GenerationStatus = isSaving ? "saving" : isGenerating ? "generating" : "idle";

  // A reference photo can silently fail Gemini's likeness policy with no reason given back, so
  // it's worth surfacing explicitly rather than leaving the user to infer it from repeated errors.
  const erroredTileCount = tiles?.filter((tile) => tile.status === "error").length ?? 0;
  const allTilesErrored = tiles !== null && tiles.length > 0 && erroredTileCount === tiles.length;
  const someTilesErrored = tiles !== null && !isGenerating && erroredTileCount > 0 && !allTilesErrored;

  const generateError = allTilesErrored
    ? hadReferenceImageRef.current
      ? adapter.copy.allFailedWithReference
      : adapter.copy.allFailed
    : someTilesErrored && hadReferenceImageRef.current
      ? adapter.copy.someFailedWithReference
      : null;

  const hasUnsavedTile = tiles !== null && !isGenerating && tiles.some((tile) => tile.status === "done" && tile.saveFailed);
  const unsavedTileError = hasUnsavedTile ? adapter.copy.unsavedTiles : null;

  const { maxSelectedStyles } = adapter;
  const toggleStyle = useCallback(
    (styleId: string) => {
      selectionTouchedRef.current = true;
      setSelectedStyleIdsState((current) => {
        if (current.includes(styleId)) return current.filter((id) => id !== styleId);
        if (current.length >= maxSelectedStyles) return current;
        return [...current, styleId];
      });
    },
    [maxSelectedStyles],
  );

  // Lazy, once per stylePresetsKey — this hook is instantiated per entity card, so a mount-fetch
  // would fire one request per card on a list page.
  const ensureStylePresetsLoaded = useCallback(() => {
    const key = stylePresetsKeyRef.current;
    if (stylePresetsFetchedKeyRef.current && stylePresetsFetchedKeyRef.current.key === key) return;
    stylePresetsFetchedKeyRef.current = { key };
    setStylePresetsError(null);
    void adapter
      .listStylePresets()
      .then((res) => {
        if (stylePresetsKeyRef.current !== key) return;
        setStylePresets(res.stylePresets);
        setDefaultBatchParams(res.defaultBatchParams);
        if (res.defaultStyleIds && !selectionTouchedRef.current) setSelectedStyleIdsState(res.defaultStyleIds);
      })
      .catch((err) => {
        if (stylePresetsKeyRef.current !== key) return;
        stylePresetsFetchedKeyRef.current = null;
        Sentry.captureException(err, { tags: { feature: `${sentryFeaturePrefix}_style_presets` } });
        setStylePresetsError("Couldn't load styles — please try again.");
      });
    // stylePresetsKey is read through the ref, but listed so a key change gives the dialog a new
    // callback identity and its load effect runs again.
  }, [adapter, sentryFeaturePrefix, stylePresetsKey]);

  // Drops what was loaded for the previous key. Any fetch still in flight for it is ignored above.
  const resetStylePresetsKeyRef = useRef(stylePresetsKey);
  useEffect(() => {
    if (resetStylePresetsKeyRef.current === stylePresetsKey) return;
    resetStylePresetsKeyRef.current = stylePresetsKey;
    selectionTouchedRef.current = false;
    setStylePresets(null);
    setStylePresetsError(null);
    setDefaultBatchParams(undefined);
    setSelectedStyleIdsState(adapter.defaultStyleIds);
  }, [stylePresetsKey, adapter.defaultStyleIds]);

  // Resolves to the tile's saved imageId, or undefined if generation or the save failed.
  const generateTile = useCallback(
    async (request: GenerateTileRequest, batchParams: TBatchParams | undefined, isRetry = false): Promise<string | undefined> => {
      const summary = lastSummaryRef.current;
      if (!summary) return undefined;
      const { stylePresetId } = request;
      const tileAnalytics = { stylePresetId, isFreeformStyle: stylePresetId === freeformStyleId, isRetry, hasReferenceImage: !!request.referenceImage };

      try {
        const response = await adapter.generate(summary, request, batchParams);
        // Falls back to the raw base64 only when the save to S3 failed (imageUrl null).
        const imageUrl = response.imageUrl ?? `data:${response.candidate.mimeType};base64,${response.candidate.base64}`;
        setTiles(
          (current) =>
            current?.map((tile) =>
              tile.stylePresetId === stylePresetId
                ? {
                    ...tile,
                    status: "done",
                    stylePresetLabel: tile.stylePresetLabel || response.candidate.stylePresetLabel,
                    imageUrl,
                    model: response.candidate.model,
                    prompt: response.candidate.prompt,
                    imageId: response.imageId ?? undefined,
                    saveFailed: !response.imageId,
                    invocationId: response.imageId ? undefined : response.invocationId,
                    pendingMimeType: response.imageId ? undefined : response.candidate.mimeType,
                    pendingBase64: response.imageId ? undefined : response.candidate.base64,
                  }
                : tile,
            ) ?? current,
        );
        analytics.tileResult({ ...tileAnalytics, outcome: response.imageId ? "success" : "success_unsaved" });
        notifyCoverSet(response);
        return response.imageId ?? undefined;
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: `${sentryFeaturePrefix}_generate` }, extra: { stylePresetId } });
        setTiles(
          (current) => current?.map((tile) => (tile.stylePresetId === stylePresetId ? { ...tile, status: "error", error: err instanceof Error ? err.message : "Couldn't generate" } : tile)) ?? current,
        );
        analytics.tileResult({ ...tileAnalytics, outcome: "error" });
        return undefined;
      }
    },
    [adapter, analytics, freeformStyleId, notifyCoverSet, sentryFeaturePrefix],
  );

  const handleRetryTile = useCallback(
    (stylePresetId: string) => {
      const tile = tiles?.find((t) => t.stylePresetId === stylePresetId);
      if (!tile || tile.status === "loading" || retryInFlightIdsRef.current.has(stylePresetId)) return;
      retryInFlightIdsRef.current.add(stylePresetId);
      const batchInputs = lastGenerateInputsRef.current;
      const referenceImagePayload = batchInputs?.referenceImagePayload ?? null;
      const isFreeformStyle = stylePresetId === freeformStyleId;
      analytics.retryTileClicked({ stylePresetId, isFreeformStyle, hasReferenceImage: !!referenceImagePayload });

      if (tile.saveFailed && tile.invocationId && tile.pendingMimeType && tile.pendingBase64) {
        const tileAnalytics = { stylePresetId, isFreeformStyle, isRetry: true, hasReferenceImage: !!referenceImagePayload };
        setTiles((current) => current?.map((t) => (t.stylePresetId === stylePresetId ? { ...t, status: "loading", saveFailed: undefined } : t)) ?? current);
        void adapter
          .saveGenerated({ invocationId: tile.invocationId, mimeType: tile.pendingMimeType, base64: tile.pendingBase64 })
          .then((result) => {
            setTiles(
              (current) =>
                current?.map((t) =>
                  t.stylePresetId === stylePresetId
                    ? { ...t, status: "done", imageId: result.imageId, imageUrl: result.imageUrl, saveFailed: false, invocationId: undefined, pendingMimeType: undefined, pendingBase64: undefined }
                    : t,
                ) ?? current,
            );
            analytics.tileResult({ ...tileAnalytics, outcome: "success" });
            notifyCoverSet(result);
            onGenerated?.();
          })
          .catch((err) => {
            Sentry.captureException(err, { tags: { feature: `${sentryFeaturePrefix}_save` }, extra: { stylePresetId } });
            setTiles((current) => current?.map((t) => (t.stylePresetId === stylePresetId ? { ...t, status: "done", saveFailed: true } : t)) ?? current);
            analytics.tileResult({ ...tileAnalytics, outcome: "success_unsaved" });
          })
          .finally(() => retryInFlightIdsRef.current.delete(stylePresetId));
        return;
      }

      setTiles((current) => current?.map((t) => (t.stylePresetId === stylePresetId ? { ...t, status: "loading", error: undefined, saveFailed: undefined } : t)) ?? current);
      void generateTile(
        {
          stylePresetId,
          stylePresetText: isFreeformStyle ? batchInputs?.description.trim() || undefined : undefined,
          referenceImage: referenceImagePayload,
          referenceImageGuidance: batchInputs?.referenceImageGuidance,
          userDescription: batchInputs?.description.trim() || undefined,
          batchId: batchInputs?.batchId,
        },
        batchInputs?.batchParams,
        true,
      )
        .then((imageId) => {
          if (imageId) onGenerated?.();
        })
        .finally(() => retryInFlightIdsRef.current.delete(stylePresetId));
    },
    [adapter, analytics, freeformStyleId, generateTile, notifyCoverSet, tiles, onGenerated, sentryFeaturePrefix],
  );

  const handleGenerate = useCallback(
    async (referenceImagePayload?: ReferenceImagePayload | null, batchParams?: TBatchParams) => {
      // Regenerating replaces every tile, which would drop billed images held only in memory.
      if (status !== "idle" || hasUnsavedTile || inFlightRef.current) return;
      const placeholders = makePlaceholderTiles(selectedStyleIds, stylePresets, freeformStyle);
      if (placeholders.length === 0) return;
      inFlightRef.current = true;

      try {
        analytics.generateClicked({
          styleCount: placeholders.length,
          isFreeformStyle: selectedStyleIds.length === 0,
          isRegenerate: tiles !== null,
          hasReferenceImage: !!referenceImagePayload,
          descriptionLength: description.length,
        }, batchParams);

        setSummarizeError(null);
        setSaveError(null);
        setTiles(placeholders);
        setTilesBatchParams(batchParams);
        hadReferenceImageRef.current = !!referenceImagePayload;

        let summary: TSummary;
        try {
          summary = await adapter.summarize({ description, referenceImage: referenceImagePayload });
        } catch (err) {
          Sentry.captureException(err, { tags: { feature: `${sentryFeaturePrefix}_summarize` }, extra: { description } });
          setTiles(null);
          setSummarizeError(err instanceof Error ? err.message : "Couldn't generate — please try again.");
          return;
        }

        lastSummaryRef.current = summary;
        const refImageGuidance = referenceImagePayload ? referenceImageGuidance : undefined;
        // Shared across the batch (and any later Retry) so the ledger groups charges into one
        // gallery line — see mergeEntityImageBatchEntries.
        const batchId = crypto.randomUUID();
        lastGenerateInputsRef.current = { referenceImagePayload, referenceImageGuidance: refImageGuidance, description, batchId, batchParams };

        // generateTile never rejects, so Promise.all here is equivalent to allSettled and keeps
        // inFlightRef locked until every tile settles.
        const imageIds = await Promise.all(
          placeholders.map((tile) =>
            generateTile(
              {
                stylePresetId: tile.stylePresetId,
                stylePresetText: tile.stylePresetId === freeformStyleId ? description.trim() || undefined : undefined,
                referenceImage: referenceImagePayload,
                referenceImageGuidance: refImageGuidance,
                userDescription: description.trim() || undefined,
                batchId,
              },
              batchParams,
            ),
          ),
        );
        if (imageIds.some(Boolean)) onGenerated?.();
      } finally {
        inFlightRef.current = false;
      }
    },
    [adapter, analytics, description, freeformStyle, freeformStyleId, generateTile, status, hasUnsavedTile, selectedStyleIds, stylePresets, referenceImageGuidance, tiles, onGenerated, sentryFeaturePrefix],
  );

  const saveImportedImages = useCallback(
    async (items: { id: string; payload: ReferenceImagePayload }[]): Promise<MultiSaveResult | null> => {
      if (status !== "idle" || items.length === 0 || inFlightRef.current) return null;
      inFlightRef.current = true;

      try {
        setSaveError(null);
        setPhase("saving");
        // Merged onto the existing map (not replaced) so a retry doesn't lose the "done" marks
        // that items from an earlier partial-failure batch already have.
        setItemSaveStatuses((current) => ({ ...current, ...Object.fromEntries(items.map((item) => [item.id, "saving"])) }));

        const settled = await Promise.allSettled(
          items.map(async (item) => {
            const result = await adapter.upload(item.payload);
            setItemSaveStatuses((current) => ({ ...current, [item.id]: "done" }));
            return result;
          }),
        );

        const succeededIds: string[] = [];
        const failedIds: string[] = [];
        let coverResult: { imageId: string; imageUrl: string } | null = null;
        settled.forEach((outcome, i) => {
          const id = items[i].id;
          if (outcome.status === "fulfilled") {
            succeededIds.push(id);
            if (outcome.value.isCover) coverResult = outcome.value;
          } else {
            failedIds.push(id);
            Sentry.captureException(outcome.reason, { tags: { feature: `${sentryFeaturePrefix}_import` }, extra: { itemId: id } });
            setItemSaveStatuses((current) => ({ ...current, [id]: "error" }));
          }
        });

        analytics.saveBatchCompleted({
          source: "upload",
          itemCount: items.length,
          succeededCount: succeededIds.length,
          failedCount: failedIds.length,
          becameCover: coverResult !== null,
        });

        if (failedIds.length === 0) {
          // Briefly hold the "done" state, or a single-item save would flip straight to gone.
          await new Promise((resolve) => setTimeout(resolve, 500));
          setPhase("idle");
          setItemSaveStatuses({});
          onSaved?.(coverResult, { hadFailures: false });
        } else {
          setPhase("idle");
          setSaveError(buildSaveErrorMessage(failedIds.length, items.length));
          // itemSaveStatuses is left as-is so callers can use succeededIds to drop only the items
          // that actually saved. Gated on succeededIds, not coverResult, since the designated cover
          // item can itself be the one that failed while later items still saved.
          if (succeededIds.length > 0) onSaved?.(coverResult, { hadFailures: true });
        }

        return { succeededIds, failedIds };
      } finally {
        inFlightRef.current = false;
      }
    },
    [adapter, analytics, onSaved, sentryFeaturePrefix, status],
  );

  return {
    status,
    isGenerating,
    isSaving,
    itemSaveStatuses,
    description,
    setDescription,
    selectedStyleIds,
    toggleStyle,
    hasGeneratableSelection: selectedStyleIds.length > 0 || !!freeformStyle,
    stylePresets,
    stylePresetsError,
    defaultBatchParams,
    ensureStylePresetsLoaded,
    referenceImageGuidance,
    setReferenceImageGuidance,
    tiles,
    tilesBatchParams,
    summarizeError,
    generateError,
    unsavedTileError,
    saveError,
    handleGenerate,
    handleRetryTile,
    saveImportedImages,
  };
}
